/*
 * DeviceDrawer – slide-in panel for adding / editing a device (leaf).
 *
 * Props
 *   open        – boolean
 *   onClose     – () => void
 *   initial     – optional existing device object (for edit), otherwise null
 *   path        – current category path as array ['cat1','cat2',…]
 */

import React, { useEffect, useState } from 'react';
import {
  Drawer, Box, Typography, TextField, Button, MenuItem,
  Select, FormControl, InputLabel, Stack,
  Switch, FormControlLabel, Autocomplete, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { saveDevice } from '../services/catalogueService';
import { TYPES, IMAGE_SETS } from '../services/staticLists';
import { listPricingLevels } from '../services/pricingService';
import { isAccessoriesArray } from '../useMillerTree';

export default function DeviceDrawer({ open, onClose, initial = null, path }) {
  /* ------------------------------------------------------------------ */
  /*  Local state                                                       */
  /* ------------------------------------------------------------------ */
  const [form, setForm] = useState(blankForm());
  const [levels, setLevels] = useState([]);  // [{level, price}]
  const [deleteStep, setDeleteStep] = useState(0);

  /* when drawer opens, prime form ----------------------------------- */
  useEffect(() => {
    if (open) {
      setForm(initial ? fromDevice(initial) : blankForm(path));
      listPricingLevels().then(setLevels);
      setDeleteStep(0);
    }
  }, [open, initial, path]);

  /* helpers ---------------------------------------------------------- */
  function handleChange(key, value) {
    setForm(f => ({ ...f, [key]: value }));
  }

  async function handleSave() {
    /* use the possibly-edited path */
    const device = toDevice(form, form.path, initial);
    await saveDevice(device);
    onClose();
  }
  
  async function executeDelete() {
    const docId = initial.id || initial.UID;
    if (docId) {
      await deleteDoc(doc(db, 'catalogue', docId));
    }
    setDeleteStep(0);
    onClose(); // This triggers the fresh data pull in App.js
  }


  /* render ----------------------------------------------------------- */
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 360, p: 3 }}>
        {/* breadcrumb – live-updates while user edits the name */}
        {form.path.length > 0 && (
          <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
            {form.path.join(' › ')}
          </Typography>
        )}

        <Typography variant="h6" gutterBottom>
          {initial ? 'Edit Device' : 'New Device'}
        </Typography>

        <Stack spacing={2}>

          {/* ──────────── Device name (final cat_x) ──────────── */}
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={form.path[form.path.length - 1] || ''}
            onChange={e => {
              const val = e.target.value;
              /* replace ONLY the final segment */
              setForm(f => ({
                ...f,
                path: [...f.path.slice(0, -1), val]
              }));
           }}
          />

          {/* Type */}
          <FormControl fullWidth size="small">
            <InputLabel id="type-label">Type</InputLabel>
            <Select
              labelId="type-label"
              label="Type"
              value={form.Type}
              onChange={e => handleChange('Type', e.target.value)}
            >
              {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Accessories Array */}
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={[]} 
            value={form.accessories || []}
            onChange={(e, newValue) => handleChange('accessories', newValue)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" size="small" label={option} key={index} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Accessories" 
                placeholder="Type accessory and press enter" 
              />
            )}
          />



          {/* Image set */}
          <FormControl fullWidth size="small">
            <InputLabel id="img-label">Image Set</InputLabel>
            <Select
              labelId="img-label"
              label="Image Set"
              value={form.image_set}
              onChange={e => handleChange('image_set', e.target.value)}
            >
              {IMAGE_SETS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Price level */}
          <FormControl fullWidth size="small">
            <InputLabel id="price-label">Price Level</InputLabel>
            <Select
              labelId="price-label"
              label="Price Level"
              value={form.price_level}
              onChange={e => handleChange('price_level', Number(e.target.value))}
            >
              {levels.map(l => (
                <MenuItem key={l.level} value={l.level}>
                  {l.level} – £{l.price.toFixed(2)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>



          {/* Numeric fields */}
          {/* {['width', 'height', 'thickness', 'extra_padding'].map(k => (
            <TextField
              key={k}
              label={k.replaceAll('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
              type="number"
              size="small"
              fullWidth
              value={form[k]}
              onChange={e => handleChange(k, Number(e.target.value))}
            />
          ))} */}

          {/* Live toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={!!form.Live}
                onChange={e => handleChange('Live', e.target.checked)}
              />
            }
            label="Live"
          />

          <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
            <Button variant="contained" onClick={handleSave} fullWidth>Save</Button>
            {initial && (
              <Button variant="outlined" color="error" onClick={() => setDeleteStep(1)} fullWidth>
                Delete
              </Button>
            )}
          </Box>
        </Stack>

          {/* Two-Step Delete Confirmation Dialog */}
          <Dialog open={deleteStep > 0} onClose={() => setDeleteStep(0)}>
            <DialogTitle>{deleteStep === 1 ? 'Delete Device?' : 'Final Confirmation'}</DialogTitle>
            <DialogContent>
              {deleteStep === 1
                ? 'Are you sure you want to delete this device?'
                : 'Are you REALLY sure? This cannot be undone and will permanently remove the device from the catalogue.'}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteStep(0)}>Cancel</Button>
              {deleteStep === 1 ? (
                <Button onClick={() => setDeleteStep(2)} color="error">Yes, Delete</Button>
              ) : (
                <Button onClick={executeDelete} color="error" variant="contained">Permanently Delete</Button>
              )}
            </DialogActions>
          </Dialog>
      </Box>
    </Drawer>
  );
}

/* -------------------------------------------------------------------- */
/*  helpers                                                            */
/* -------------------------------------------------------------------- */
function blankForm(path = []) {
  return {
    Type: '',
    image_set: '',
    price_level: 1,
    // width: 0,
    // height: 0,
    // thickness: 0,
    // extra_padding: 0,
    Live: false,
    accessories: ['No accessories'],
    path
  };
}

function fromDevice(d) {
  const rawPath = extractPath(d);
  let acc = [];
  const nextCat = d[`cat_${rawPath.length + 1}`];
  if (isAccessoriesArray(nextCat)) {
    acc = nextCat;
  }
  if (acc.length === 0) {
    acc = ['No accessories'];
  }

  return {
    Type: d.Type || d.type || '',
    image_set: d.image_set || '',
    price_level: d.price_level || 1,
    // width: d.width || 0,
    // height: d.height || 0,
    // thickness: d.thickness || 0,
    // extra_padding: d.extra_padding || 0,
    Live: !!d.Live,              /* keep existing state */
    accessories: acc,
    path: rawPath
  };
}

function toDevice(form, path, existing) {
  const base = existing ? { ...existing } : {};
  const out = {
    UID: base.UID || '',
    Live: !!form.Live,
    Type: form.Type || '',
    image_set: form.image_set || '',
    // price_level: form.price_level || 1,
    // width: form.width || 0,
    // height: form.height || 0,
    // thickness: form.thickness || 0,
    // extra_padding: form.extra_padding || 0
    price_level: form.price_level || 1
  };

  // 1. Initialize all cats to empty strings
  for (let i = 1; i <= 10; i++) {
    out[`cat_${i}`] = '';
  }

  // 2. Map the true path into the categories
  path.forEach((v, i) => { out[`cat_${i + 1}`] = v; });

  // 3. Handle Accessories Array
  if (form.accessories && form.accessories.length > 0) {
    // Make it bulletproof against whitespace
    const isOnlyNoAcc = form.accessories.length === 1 && 
                        form.accessories[0].trim().toLowerCase() === 'no accessories';
    
    if (!isOnlyNoAcc) {
      out[`cat_${path.length + 1}`] = form.accessories;
    }
  }

  // 4. Scrub empty strings to keep Firestore clean (removes the field completely)
  for (let i = 1; i <= 10; i++) {
    if (out[`cat_${i}`] === '') {
      delete out[`cat_${i}`];
    }
  }

  return out;
}


function extractPath(d) {
  const arr = [];
  for (let i = 1; i <= 10; i++) {
    const v = d[`cat_${i}`];
    if (v && !isAccessoriesArray(v)) arr.push(v); 
    else break;
  }
  return arr;
}